import React, { createContext, useContext, useState } from 'react';
import { savePreQuestionnaire, savePostQuestionnaire, saveEmailSignup } from '../utils/api';

const QuestionnaireContext = createContext();

export const useQuestionnaireContext = () => {
    const context = useContext(QuestionnaireContext);
    if (!context) {
        throw new Error('useQuestionnaireContext must be used within a QuestionnaireProvider');
    }
    return context;
};

export const QuestionnaireProvider = ({ children }) => {
    const [preQuestionnaireData, setPreQuestionnaireData] = useState(() => {
        const saved = localStorage.getItem('preQuestionnaireData');
        return saved ? JSON.parse(saved) : null;
    });
    const [postQuestionnaireData, setPostQuestionnaireData] = useState(() => {
        const saved = localStorage.getItem('postQuestionnaireData');
        return saved ? JSON.parse(saved) : null;
    });
    const [hasCompletedPreQuestionnaire, setHasCompletedPreQuestionnaire] = useState(() => {
        return localStorage.getItem('hasCompletedPreQuestionnaire') === 'true';
    });
    const [hasCompletedPostQuestionnaire, setHasCompletedPostQuestionnaire] = useState(() => {
        return localStorage.getItem('hasCompletedPostQuestionnaire') === 'true';
    });
    const [hasPaid, setHasPaid] = useState(() => {
        return localStorage.getItem('hasPaid') === 'true';
    });
    const [canAccessChatbot, setCanAccessChatbot] = useState(() => {
        return localStorage.getItem('hasCompletedPreQuestionnaire') === 'true';
    });
    const [hasProvidedEmail, setHasProvidedEmail] = useState(() => {
        return localStorage.getItem('hasProvidedEmail') === 'true';
    });
    const [canDownloadPDF, setCanDownloadPDF] = useState(() => {
        return localStorage.getItem('hasCompletedPostQuestionnaire') === 'true' ||
            localStorage.getItem('hasPaid') === 'true' ||
            localStorage.getItem('hasProvidedEmail') === 'true';
    });

    const completePreQuestionnaire = async (data) => {
        try {
            const result = await savePreQuestionnaire(data);
            console.log('Pre-questionnaire saved:', result);
        } catch (error) {
            console.error('Error saving pre-questionnaire:', error);
        }

        // Update local state regardless of API success/failure
        setPreQuestionnaireData(data);
        setHasCompletedPreQuestionnaire(true);
        setCanAccessChatbot(true);
        localStorage.setItem('preQuestionnaireData', JSON.stringify(data));
        localStorage.setItem('hasCompletedPreQuestionnaire', 'true');
    };

    const completePostQuestionnaire = async (data) => {
        try {
            const result = await savePostQuestionnaire(data);
            console.log('Post-questionnaire saved:', result);
        } catch (error) {
            console.error('Error saving post-questionnaire:', error);
        }

        // Update local state regardless of API success/failure
        setPostQuestionnaireData(data);
        setHasCompletedPostQuestionnaire(true);
        // Enable PDF download immediately after completing post questionnaire
        setCanDownloadPDF(true);
        localStorage.setItem('postQuestionnaireData', JSON.stringify(data));
        localStorage.setItem('hasCompletedPostQuestionnaire', 'true');
    };

    const completePurchase = () => {
        setHasPaid(true);
        // Enable PDF download immediately after payment
        setCanDownloadPDF(true);
        localStorage.setItem('hasPaid', 'true');
    };

    const submitEmail = async (data) => {
        try {
            // Send email and name to backend
            const result = await saveEmailSignup(data.name, data.email, 'pdf_download', 'access');
            console.log('Email submitted:', result);
        } catch (error) {
            console.error('Error saving email:', error);
            throw error; // Re-throw to let the modal handle the error
        }

        // Update local state only on success
        setHasProvidedEmail(true);
        setCanDownloadPDF(true);
        localStorage.setItem('hasProvidedEmail', 'true');
        localStorage.setItem('userEmail', data.email);
        localStorage.setItem('userName', data.name);
    };

    const resetQuestionnaires = () => {
        setPreQuestionnaireData(null);
        setPostQuestionnaireData(null);
        setHasCompletedPreQuestionnaire(false);
        setHasCompletedPostQuestionnaire(false);
        setHasPaid(false);
        setHasProvidedEmail(false);
        setCanAccessChatbot(false);
        setCanDownloadPDF(false);
        localStorage.removeItem('preQuestionnaireData');
        localStorage.removeItem('postQuestionnaireData');
        localStorage.removeItem('hasCompletedPreQuestionnaire');
        localStorage.removeItem('hasCompletedPostQuestionnaire');
        localStorage.removeItem('hasPaid');
        localStorage.removeItem('hasProvidedEmail');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userName');
    };

    const value = {
        // Data
        preQuestionnaireData,
        postQuestionnaireData,

        // Status flags
        hasCompletedPreQuestionnaire,
        hasCompletedPostQuestionnaire,
        hasPaid,
        hasProvidedEmail,
        canAccessChatbot,
        canDownloadPDF,

        // Actions
        completePreQuestionnaire,
        completePostQuestionnaire,
        completePurchase,
        submitEmail,
        resetQuestionnaires
    };

    return (
        <QuestionnaireContext.Provider value={value}>
            {children}
        </QuestionnaireContext.Provider>
    );
};

export default QuestionnaireContext;
